/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import {
    qnaMakerQnaSwaggerToQnaStore,
    qnaMakerSynonymSwaggerToSynonymStore
} from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';

const messages = defineMessages({
    loadingSynonymsFailedTitle: {
        id: 'qnaMaker.useQnaAssets.loadingSynonymsFailedTitle',
        defaultMessage: 'Failed to load synonym groups'
    },
    loadingQnasBaseFailedTitle: {
        id: 'qnaMaker.qnaMakerQnaList.loadingQnasBaseFailedTitle',
        defaultMessage: 'Failed to load QnA pairs'
    }
});

export const useQnaAssets = () => {
    const { uiStore, dispatcher, rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const { qnasJsonData } = rootStore.currentQnaMakerProjectStore;
    const { synonymsJsonData } = rootStore.currentQnaMakerProjectStore;

    const qnaUiStore = useLocalStore<QnaMakerQnaPairsUiStore>(() => new QnaMakerQnaPairsUiStore(uiStore));
    const synonymUiStore = useLocalStore<QnaMakerSynonymsUiStore>(() => new QnaMakerSynonymsUiStore());

    const [areQnasLoading, setAreQnasLoading] = React.useState(true);
    const [areSynonymsLoading, setAreSynonymsLoading] = React.useState(true);

    const qnasFetchingTracker = useProgressTracker({
        onComplete: () => {
            qnaUiStore.setQnaPairsJson(qnasJsonData);
            qnaUiStore.setFetchedQnaPairsJson(qnasJsonData);
            qnaUiStore.setDisplayedQnaPairStores(qnasJsonData.map(qnaMakerQnaSwaggerToQnaStore));
            setAreQnasLoading(false);
        }
    });
    React.useEffect(() => {
        qnaUiStore.setQnaPairsJson(qnasJsonData);
        qnaUiStore.setFetchedQnaPairsJson(qnasJsonData);
        qnaUiStore.setDisplayedQnaPairStores(qnasJsonData.map(qnaMakerQnaSwaggerToQnaStore));
    }, [qnasJsonData]);

    const synonymsFetchingTracker = useProgressTracker({
        onComplete: () => {
            synonymUiStore.setSynonymsJson(synonymsJsonData);
            synonymUiStore.setFetchedSynonymsJson(synonymsJsonData);
            synonymUiStore.setDisplayedSynonymStores(synonymsJsonData.map(qnaMakerSynonymSwaggerToSynonymStore));
            setAreSynonymsLoading(false);
        }
    });
    React.useEffect(() => {
        synonymUiStore.setSynonymsJson(synonymsJsonData);
        synonymUiStore.setFetchedSynonymsJson(synonymsJsonData);
        synonymUiStore.setDisplayedSynonymStores(synonymsJsonData.map(qnaMakerSynonymSwaggerToSynonymStore));
    }, [synonymsJsonData]);

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerAssets', {
            tracker: qnasFetchingTracker,
            toastData: {
                failed: {
                    title: localize(messages.loadingQnasBaseFailedTitle),
                    content: qnasFetchingTracker.error?.message
                }
            }
        });

        return () => {
            rootStore.currentQnaMakerProjectStore?.resetQnaPairsLoadedState();
        };
    }, []);

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerAssets', {
            tracker: synonymsFetchingTracker,
            assetsType: 'synonyms',
            toastData: {
                failed: {
                    title: localize(messages.loadingSynonymsFailedTitle),
                    content: synonymsFetchingTracker.error?.message
                }
            }
        });

        return () => {
            rootStore.currentQnaMakerProjectStore?.resetSynonymsLoadedState();
        };
    }, []);

    const isLoading = React.useMemo(() => areQnasLoading || areSynonymsLoading, [areQnasLoading, areSynonymsLoading]);

    return { qnaUiStore, synonymUiStore, isLoading };
};
